import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import { Patient } from "../services/patient.service";

interface DeletePatientModalProps {
  open: boolean;
  patient: Patient | null;
  onClose: () => void;
  onConfirm: (firstName: string, lastName: string) => Promise<void>;
}

export const DeletePatientModal: React.FC<DeletePatientModalProps> = ({
  open,
  patient,
  onClose,
  onConfirm,
}) => {
  const [confirmFirstName, setConfirmFirstName] = useState("");
  const [confirmLastName, setConfirmLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    if (!loading) {
      setConfirmFirstName("");
      setConfirmLastName("");
      setError("");
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!patient) return;

    setLoading(true);
    setError("");

    try {
      await onConfirm(confirmFirstName, confirmLastName);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to delete patient");
    } finally {
      setLoading(false);
    }
  };

  const isValid =
    confirmFirstName.trim() !== "" && confirmLastName.trim() !== "";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' gap={1}>
          <WarningIcon color='error' />
          <Typography variant='h6'>Delete Patient Account</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity='error' sx={{ mb: 3 }}>
          <Typography variant='body2' fontWeight='bold'>
            WARNING: This action is permanent and cannot be undone!
          </Typography>
          <Typography variant='body2' sx={{ mt: 1 }}>
            This will permanently delete all data associated with this patient,
            including:
          </Typography>
          <Box component='ul' sx={{ mt: 1, pl: 2 }}>
            <li>Account information</li>
            <li>Medical records and readings</li>
            <li>Device data</li>
            <li>Provider associations</li>
            <li>Health events and medications</li>
            <li>All historical data and logs</li>
          </Box>
        </Alert>

        {patient && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              <strong>Patient:</strong> {patient.lastname}, {patient.firstname}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              <strong>Chart ID:</strong> {patient.chartID}
            </Typography>
          </Box>
        )}

        <Typography variant='body2' sx={{ mb: 2 }}>
          To confirm deletion, please type the patient's{" "}
          <strong>first name</strong> and <strong>last name</strong> exactly as
          shown above:
        </Typography>

        <TextField
          fullWidth
          label='First Name'
          value={confirmFirstName}
          onChange={(e) => setConfirmFirstName(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
          autoFocus
        />

        <TextField
          fullWidth
          label='Last Name'
          value={confirmLastName}
          onChange={(e) => setConfirmLastName(e.target.value)}
          disabled={loading}
        />

        {error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!isValid || loading}
          color='error'
          variant='contained'
        >
          {loading ? "Deleting..." : "Delete Patient"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
